import { FileText } from 'lucide-react'
import { DocumentsToolMessage } from '../ScreenataChatToolMessages'
import { FileExtensionIcon } from '@/utils/file-icons'
import {
  BagDelegate,
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const completedIcon = (
  <FileText className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

function DocumentList({
  items,
  emptyLabel,
}: {
  items: Array<{ id?: string | null; title: string }>
  emptyLabel: string
}) {
  if (items.length === 0) {
    return (
      <div className='text-muted-foreground/80 py-1 text-xs'>{emptyLabel}</div>
    )
  }
  return (
    <div className='mt-1 w-full overflow-hidden rounded-md border border-border bg-card'>
      <div className='divide-y divide-border'>
        {items.map((item, index) => (
          <div
            key={`${item.id ?? item.title}-${index}`}
            className='flex items-center gap-2 px-2.5 py-1.5'
          >
            <FileExtensionIcon filename={item.title} className='size-4' />
            <span className='truncate text-[12px] text-text-secondary'>
              {item.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// saveDraft has an approval flow + DocumentSaveDraftToolMessage card → bag.
export function DocumentsSaveDraftToolMessage(props: ToolPartProps) {
  return (
    <BagDelegate
      Component={DocumentsToolMessage}
      action='saveDraft'
      {...props}
    />
  )
}

export function DocumentsListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const documents: Array<Record<string, unknown>> = Array.isArray(
    state.result?.items
  )
    ? state.result.items
    : Array.isArray(state.result?.documents)
      ? state.result.documents
      : []
  const total =
    typeof state.result?.total === 'number'
      ? state.result.total
      : documents.length
  const filterDetail = pickString(state.args?.category, state.args?.status)
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading documents...'
        completedLabel={
          total !== null
            ? `${plural(total, 'document')} loaded`
            : 'Documents loaded'
        }
        loadingDetail={filterDetail}
        completedDetail={filterDetail}
        completedIcon={completedIcon}
      />
      {state.isComplete && !state.isError && !isToolError(state) ? (
        <DocumentList
          emptyLabel='No documents found'
          items={documents.map((doc) => ({
            id: pickString(doc.id),
            title: pickString(doc.title) ?? 'Untitled document',
          }))}
        />
      ) : null}
    </>
  )
}

export function DocumentsSearchToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const documents: Array<Record<string, unknown>> = Array.isArray(
    state.result?.items
  )
    ? state.result.items
    : Array.isArray(state.result?.documents)
      ? state.result.documents
      : []
  const total =
    typeof state.result?.total === 'number'
      ? state.result.total
      : documents.length
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Searching documents...'
        completedLabel={
          total !== null
            ? `Found ${plural(total, 'document')}`
            : 'Document search complete'
        }
        loadingDetail={pickString(state.args?.query)}
        completedDetail={total === 0 ? null : pickString(state.args?.query)}
        completedIcon={completedIcon}
      />
      {state.isComplete && !state.isError && !isToolError(state) ? (
        <DocumentList
          emptyLabel='No matching documents'
          items={documents.map((doc) => ({
            id: pickString(doc.id),
            title: pickString(doc.title) ?? 'Untitled document',
          }))}
        />
      ) : null}
    </>
  )
}

export function DocumentsGetByIdToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const document = state.result?.document ?? state.result
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading document...'
        completedLabel='Document loaded'
        loadingDetail={pickString(state.args?.id)}
        completedDetail={pickString(document?.title, state.args?.id)}
        completedIcon={completedIcon}
      />
      {state.isComplete && !state.isError && !isToolError(state) && document ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(
            part,
            `document-${pickString(document.id, state.args?.id) ?? 'detail'}`
          )}
          payload={{
            entityType: 'document',
            toolName: 'documentsGetById',
            title: pickString(document.title) ?? 'Untitled document',
            subtitle: pickString(document.id, state.args?.id),
            description: pickString(document.description, document.excerpt),
            fields: [
              {
                label: 'Category',
                value: formatToolValue(document.category),
              },
              {
                label: 'Status',
                value: formatToolValue(document.status),
                tone: normalizeTone(document.status),
              },
              {
                label: 'Source',
                value: formatToolValue(document.sourceType),
              },
              {
                label: 'Controls',
                value: Array.isArray(document.controlRefs)
                  ? document.controlRefs.join(', ')
                  : null,
              },
              {
                label: 'Updated',
                value: formatToolValue(document.updatedAt),
              },
            ],
            sections: [
              {
                title: 'Content',
                content: formatToolValue(document.content),
              },
            ],
          }}
          systemDetail={
            pickString(document.id, state.args?.id)
              ? {
                  entityType: 'document',
                  entityId: pickString(document.id, state.args?.id)!,
                  workspaceId: '',
                }
              : null
          }
        />
      ) : null}
    </>
  )
}

// Legacy alias kept for old runs.
export function VaultDocumentToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Working with documents...'
      completedLabel='Documents loaded'
      completedIcon={completedIcon}
    />
  )
}
